import {
  DARK_ID,
  LIGHT_ID,
  applyTheme,
  getTheme,
  isLightTheme,
  onThemeChange,
  saveTheme,
  type Theme,
} from "../config/themes.ts";

/* ───────────────────────────────────────────────────────────────
   Light / dark toggle, top right.

   It only ever switches between the two editorial palettes (ink and
   paper). The CRT phosphors stay reachable through `/theme` — they're
   easter eggs, not part of the front-door UI.

   If a CRT palette is active, the button reads its background
   luminance and offers the opposite side, so pressing it always lands
   somewhere sensible instead of no-opping.

   Icons are inline SVG rather than ☀/☾ glyphs: the self-hosted fonts
   are subset to latin, so those characters would fall back to whatever
   the system happens to have.
   ─────────────────────────────────────────────────────────────── */

const SUN = `<svg viewBox="0 0 24 24" width="15" height="15" fill="none"
  stroke="currentColor" stroke-width="1.6" stroke-linecap="round" aria-hidden="true">
  <circle cx="12" cy="12" r="4.2"/>
  <path d="M12 2.4v2.2M12 19.4v2.2M2.4 12h2.2M19.4 12h2.2
           M5.2 5.2l1.6 1.6M17.2 17.2l1.6 1.6M18.8 5.2l-1.6 1.6M6.8 17.2l-1.6 1.6"/>
</svg>`;

const MOON = `<svg viewBox="0 0 24 24" width="15" height="15" fill="none"
  stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"
  aria-hidden="true">
  <path d="M20 13.4A8.2 8.2 0 1 1 10.6 4a6.6 6.6 0 0 0 9.4 9.4z"/>
</svg>`;

export class ThemeToggle {
  private button: HTMLButtonElement;

  constructor(mount: HTMLElement) {
    this.button = document.createElement("button");
    this.button.className = "theme-toggle";
    this.button.type = "button";
    this.button.addEventListener("click", () => this.toggle());
    mount.append(this.button);

    this.sync();
    onThemeChange(() => this.sync());
  }

  private toggle(): void {
    const current = getTheme(document.documentElement.dataset.theme ?? "");
    const goingLight = !current || !isLightTheme(current);
    const next = getTheme(goingLight ? LIGHT_ID : DARK_ID);
    if (!next) return;
    applyTheme(next);
    saveTheme(next.id);
  }

  /** Reflect whichever palette is active — including one set from /theme. */
  private sync(): void {
    const current = getTheme(document.documentElement.dataset.theme ?? "");
    const isLight = current ? isLightTheme(current) : false;
    // Show the mode you'd get, which is the convention people expect.
    const label = isLight ? "Switch to dark mode" : "Switch to light mode";
    this.button.innerHTML = isLight ? MOON : SUN;
    this.button.setAttribute("aria-label", label);
    this.button.title = label;
  }

  /** Exposed so a future command could hide the button if needed. */
  get element(): HTMLButtonElement {
    return this.button;
  }
}

export type { Theme };
